import React, { useImperativeHandle, useRef, useState } from "react";
import { useAsyncState } from "@shared/modules/utils/customHooks";

import TextField from "./TextField";
import NumberField from "./NumberField";
import DisplayField from "./DisplayField";
import SelectField from "./SelectField";
import RadioInput from "./RadioInput";
import SwitchField from "./SwitchField";
import OtpField from "./OtpField";
import DateTimeField from "./DateTimeField";
import { validationService, getValidators } from "./constants";

import "@shared/styles/utils/_form-field.scss";
import DebitCardInput from "./CardField";
import DataStore from "@shared/services/DataStore";
import TextField2 from "./TextField2";
import OtpField2 from "./OtpField2";
import ToastUtil from "../ToastUtil";
import NumberField2 from "./NumberField2";
import SelectField2 from "./SelectField2";
import TextGroupField from "./TextGroup";
import DisplayField2 from "./DisplayField2";

const formFields = {
    text: TextField,
    text2: TextField2,
    number: NumberField,
    number2: NumberField2,
    display: DisplayField,
    display2: DisplayField2,
    select: SelectField,
    select2: SelectField2,
    radio: RadioInput,
    switch: SwitchField,
    otp: OtpField,
    otp2: OtpField2,
    card: DebitCardInput,
    textgroup: TextGroupField,
    date: DateTimeField
};

const VIRTUAL_KEYBOARD_LANGUAGES = ["en", "ar","en2"];
let currLocation = window.location.href;
const FormField = React.forwardRef(
    (
        {
            controlled = false,
            type = currLocation.includes("v2")?"text2":"text",
            id = `uniqId-${+new Date()}`,
            value = "",
            onChange,
            disabled,
            required,
            validationProfiles, // registered common validation methods
            validator, // custom validation method
            inputValidations, // validate user input before setting it in state
            liveValidations, // validate on every input update
            fallbackField = "text", //specially added for masking values of formfields like card, otp, switch, etc.
            ...restProps
        },
        ref
    ) => {
        // options that are to be passed ahead
        let fieldOpts = {
            type,
            disabled,
            ...restProps
        };

        const inputRef = useRef();

        const lastWrittenVal = useRef(value); // track uncontrolled state, async-state has stale values in isValid

        const [_value, setValue] = useAsyncState("");
        const [errorMsg, setErrorMsg] = useState("");

        const focus = () => inputRef.current?.focus();

        const getVal = () => (controlled ? value : lastWrittenVal.current !== undefined ? lastWrittenVal.current : _value);

        const setVal = val => {
            lastWrittenVal.current = val;
            setValue(val);
        }

        const setError = msg => {
            setErrorMsg(msg);
            if(type==="otp2"){
                ToastUtil.error(msg)
            }
        }

        const clearError = () => setErrorMsg("");

        const val = val => {
            if (val === undefined) return getVal();
            return setVal(val);
        };

        const isValid = (hideError = false) => {
            if ((required === false && getVal() === "") || disabled) {
                clearError();
                return true;
            }
            let validators = [
                ...(validator ? [validator] : []),
                ...getValidators(validationProfiles ? ["default", ...validationProfiles] : ["default"])
            ];
            let validationResults = validationService.validate(validators, {
                value: getVal(),
                config: fieldOpts
            });
            let errors = validationResults.filter(v => !v.valid);
            if (!hideError && errors.length) setError(errors[0].errorMsg);
            return errorMsg ? false : errors.length === 0; /* In case error is set explicitly */
        };

        const isInputValid = value => {
            let validators = getValidators(inputValidations);
            let validationResults = validationService.validate(validators, {
                value,
                config: fieldOpts
            });
            return validationResults.every(v => v.valid === true);
        };

        const liveValidate = value => {
            let liveValidators = getValidators(liveValidations || ["default"]);
            let validationResults = validationService.validate(liveValidators, {
                value,
                config: fieldOpts
            });
            let errors = validationResults.filter(v => v.valid === false);
            if (errors.length) setError(errors[0].errorMsg);
        };

        const onChangeInput = async (newVal, notFromUser) => {
            if (inputValidations && !isInputValid(newVal)) return;

            clearError();
            if (controlled) {
                if (liveValidations) liveValidate(newVal);
                if (onChange) onChange(newVal);
                return;
            }
            await setVal(newVal);
            if (liveValidations) liveValidate(newVal);
            if (onChange && !notFromUser) onChange(newVal);
        };

        useImperativeHandle(ref, () => ({
            focus,
            isValid,
            val,
            setError,
            clearError
        }));

        let Field = formFields[type];
        if (!Field) {
            const isValidFallbackField = Object.keys(formFields).find(fieldType => fieldType === fallbackField);
            Field = !!isValidFallbackField ? formFields[fallbackField] : "text";
            console.error(`FormField type : ${type} not found, falling back to 'text'`);
        }

        if (type === "text" || type === "select") {
            //if virtualKeyboardLang is not explicitly passed as props,
            //language is set user's language preference
            let { virtualKeyboardLang = "" } = fieldOpts;
            if (!virtualKeyboardLang || !VIRTUAL_KEYBOARD_LANGUAGES.includes(virtualKeyboardLang)) {
                const userLanguagePreference = DataStore.getData().prefs?.lang || "en";
                fieldOpts = { ...fieldOpts, virtualKeyboardLang: userLanguagePreference };
                // console.log(
                //     `FormField virtualKeyboardLang : ${virtualKeyboardLang} not found, falling back to user preference '${userLanguagePreference}'`
                // );
            }
        }

        return (
            <Field
                ref={inputRef}
                value={controlled ? value : _value}
                onChangeInput={onChangeInput}
                errorMsg={errorMsg} // later accomodate warning and default texts
                {...fieldOpts}
            />
        );
    }
);

export default FormField;
